import type { MillsheetSortConfig } from '@mbfab/shared'
import { isNull, isPresent, isSome } from '@mbfab/shared'
import { PencilLine, Printer, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'

import { BulkEditDialog } from './bulk-edit-dialog'
import type { MillsheetBulkEditFields } from './bulk-edit-dialog'
import type { MillsheetFilterState } from './filter-state'
import { MillsheetPrintDialog } from './millsheet-print-dialog'
import type { PrintDialogState, S3ObjectForPrint } from './print/print-types'
import type { Feedback, MillsheetColumnKey, MillsheetRow } from './types'
import { MILLSHEET_COLUMN_LABELS } from './types'

import { DataTableLink } from '@/components/app/data-table-link'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { MAX_PRINT_SELECTION_COUNT, printSelectionLimitMessage } from '@/components/ui/data-table/bulk-operation-limits'
import type {
  DataTableColumn,
  DataTableExpandable,
  DataTableRowSelection,
  DataTableSorter,
} from '@/components/ui/data-table/data-table'
import { DataTable } from '@/components/ui/data-table/data-table'
import { DataTableFileIcon } from '@/components/ui/data-table/file-cell'
import { SelectAllResultsBanner } from '@/components/ui/data-table/select-all-banner'
import { useTableSelection } from '@/components/ui/data-table/use-table-selection'
import { valueOrDash } from '@/components/ui/data-table/value-or-dash'
import { CompactDateInput } from '@/components/ui/date-input'
import { ResultBanner } from '@/components/ui/result-banner'
import { notify } from '@/lib/notify'
import { RETRY_HINT } from '@/lib/run-mutation'

// 明細単位のインライン編集で更新できる項目。工区・節・部位は明細の属性（親行の表示は集約値）
export type MillsheetItemUpdateFields = {
  thicknessDiameter?: string
  endorsementCompany?: string | null
  endorsementDate?: string | null
  constructionArea?: string
  member?: string
  buildingPart?: string
}

export type MillsheetBulkTarget = { ids: string[] } | { filter: MillsheetSearch }
export type MillsheetSearch = { filter: MillsheetFilterState; freeWord: string }

// 証明書番号（no）は左固定のキー列として別枠で描画するため、この一覧には含めない
// （docs/develop/client_ui/components/data-table.md「列の表示」・orders-table の参照番号列と同型）
const BASE_COLUMNS: MillsheetColumnKey[] = [
  'constructionName',
  'constructionArea',
  'member',
  'buildingPart',
  'steelTypes',
  'sectionTypes',
  'manufacturerName',
  'tradingCompany',
  'issuedDate',
  'orderNumber',
  'innerNo',
  'sizes',
  'totalWeight',
  'fileUrl',
  'attachmentFileUrl',
  'shearingCertificateFileUrl',
  'customer',
  'submissionDate',
]

// セル内インライン編集の入力には clearable を付けない（絞り込みバーの StringField とは異なる判断）。
// クリアボタンが行ごとに並ぶと一覧の密度が上がって見づらくなるうえ、
// 「値を空にする」操作自体がこれらの項目では業務上ほぼ発生しないため
function InlineTextInput({
  defaultValue,
  ariaLabel,
  onCommit,
}: {
  defaultValue: string
  ariaLabel: string
  onCommit: (next: string) => void
}) {
  return (
    <input
      type="text"
      defaultValue={defaultValue}
      aria-label={ariaLabel}
      className="w-full min-w-24 rounded-md border border-input bg-card px-2 py-1 text-sm"
      onBlur={(e) => {
        if (e.target.value !== defaultValue) {
          onCommit(e.target.value)
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur()
        }
      }}
    />
  )
}

// 工区・節・部位は明細ごとの属性で、親行の値は配下の明細のまとめ（読み取り専用）。
// 親行で編集すると全明細が同じ値で上書きされてしまうため、編集は展開した明細行か一括編集で行う
function AggregatedCell({ value }: { value: string }) {
  return (
    <span title="配下の明細の値をまとめて表示しています。明細ごとの編集は行を展開してください">
      {valueOrDash(value)}
    </span>
  )
}

function PdfCountButton({
  ids,
  fileName,
  onDownload,
}: {
  ids: string[]
  fileName: string
  onDownload: (s3ObjectId: string) => Promise<string | null>
}) {
  if (ids.length === 0) {
    return <span className="text-muted-foreground">—</span>
  }
  return (
    <div className="flex flex-wrap gap-1">
      {ids.map((id) => (
        <DataTableFileIcon
          key={id}
          kind="pdf"
          fileName={fileName}
          testId={`millsheet-pdf-link-${id}`}
          onOpen={() => {
            void (async () => {
              const url = await onDownload(id)
              if (isSome(url)) {
                window.open(url, '_blank', 'noopener,noreferrer')
              }
            })()
          }}
        />
      ))}
    </div>
  )
}

export function MillsheetsTable({
  millsheets,
  loading,
  sorters,
  onChangeSorters,
  orderMatchingEnabled,
  millsheetSortConfig,
  feedback,
  onDismissFeedback,
  onUpdateMillsheet,
  onUpdateMillsheetItem,
  onBulkEdit,
  onDelete,
  onDownloadS3Object,
  onFetchPrintImages,
  pagination,
  onChangePage,
  filter,
  conditionsKey,
  onCountAllResults,
  onFetchResultsForPrint,
  actionsDisabled = false,
}: {
  millsheets: MillsheetRow[]
  loading: boolean
  // 複数列ソート（DataTable 共通の型）。並び替えの実行は millsheet-workspace 側の
  // sortMillsheetRows（filter-state.ts）が担い、このコンポーネントは DataTable への受け渡しに徹する
  sorters: DataTableSorter[]
  onChangeSorters: (next: DataTableSorter[]) => void
  orderMatchingEnabled: boolean
  millsheetSortConfig: MillsheetSortConfig
  feedback: Feedback | null
  onDismissFeedback: () => void
  onUpdateMillsheet: (id: string, fields: { innerNo?: string; submissionDate?: string | null }) => Promise<boolean>
  onUpdateMillsheetItem: (id: string, fields: MillsheetItemUpdateFields) => Promise<boolean>
  onBulkEdit: (target: MillsheetBulkTarget, fields: MillsheetBulkEditFields, count: number) => Promise<boolean>
  onDelete: (id: string) => Promise<boolean>
  onDownloadS3Object: (s3ObjectId: string) => Promise<string | null>
  onFetchPrintImages: (s3ObjectIds: string[]) => Promise<S3ObjectForPrint[] | null>
  pagination: { page: number; pageSize: number; totalCount: number }
  onChangePage: (page: number) => void
  filter: MillsheetSearch
  conditionsKey: string
  onCountAllResults: (filter: MillsheetSearch) => Promise<number | null>
  onFetchResultsForPrint: (
    target: MillsheetBulkTarget,
    sorters: DataTableSorter[],
    count: number,
  ) => Promise<MillsheetRow[] | null>
  // 絞り込みの入力待ち中など、表示中の一覧が条件と一致していない間は一括操作を止める
  actionsDisabled?: boolean
}) {
  const columns = useMemo((): MillsheetColumnKey[] => {
    if (orderMatchingEnabled) {
      return [...BASE_COLUMNS, 'steelOrder', 'actions']
    }
    return [...BASE_COLUMNS, 'actions']
  }, [orderMatchingEnabled])

  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const [printState, setPrintState] = useState<PrintDialogState>({ status: 'closed' })
  const [bulkPending, setBulkPending] = useState(false)
  const [bulkFeedback, setBulkFeedback] = useState<Feedback | null>(null)
  const [confirmedCount, setConfirmedCount] = useState(0)
  const [confirmedTarget, setConfirmedTarget] = useState<MillsheetBulkTarget | null>(null)
  const [confirmedPrintCount, setConfirmedPrintCount] = useState(0)
  const totalPages = Math.max(1, Math.ceil(pagination.totalCount / pagination.pageSize))
  const pageIds = millsheets.map((millsheet) => millsheet.id)

  const {
    selection,
    selectedCount,
    allPageSelected,
    somePageSelected,
    isSelected,
    toggleSelectAllPage,
    toggleSelect,
    selectAllResults,
    clearSelection,
    deselect,
  } = useTableSelection({ pageIds, filter, filterKey: conditionsKey, totalCount: pagination.totalCount })
  const actionTarget: MillsheetBulkTarget =
    selection.mode === 'all' ? { filter: selection.filter } : { ids: selection.ids }

  const toggleExpand = (id: string) => {
    setExpandedIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  }

  const countActionTarget = async (target: MillsheetBulkTarget): Promise<number | null> => {
    if ('ids' in target) {
      return target.ids.length
    }
    const count = await onCountAllResults(target.filter)
    if (isNull(count)) {
      const message = '対象件数の確認に失敗しました'
      setBulkFeedback({ type: 'error', message })
      notify.error(message, RETRY_HINT)
    }
    return count
  }

  const openBulkEdit = async () => {
    if (bulkPending) {
      return
    }
    setBulkPending(true)
    const target = actionTarget
    try {
      const count = await countActionTarget(target)
      if (isSome(count)) {
        setConfirmedTarget(target)
        setConfirmedCount(count)
        setBulkEditOpen(true)
      }
    } finally {
      setBulkPending(false)
    }
  }

  const openPrint = async () => {
    if (bulkPending) {
      return
    }
    setBulkPending(true)
    const target = actionTarget
    const targetSorters = sorters
    try {
      const count = await countActionTarget(target)
      if (!isSome(count)) {
        return
      }
      if (count > MAX_PRINT_SELECTION_COUNT) {
        const message = printSelectionLimitMessage()
        setBulkFeedback({ type: 'error', message })
        notify.error(message)
        return
      }
      setConfirmedPrintCount(count)
      const selected = await onFetchResultsForPrint(target, targetSorters, count)
      if (!isSome(selected)) {
        const message = '印刷対象の取得に失敗しました'
        setBulkFeedback({ type: 'error', message })
        notify.error(message, RETRY_HINT)
        return
      }
      if (selected.length > MAX_PRINT_SELECTION_COUNT) {
        const message = printSelectionLimitMessage()
        setBulkFeedback({ type: 'error', message })
        notify.error(message)
        return
      }
      const s3ObjectIds = selected.flatMap((row) => row.s3Objects.map((s3) => s3.id))
      setPrintState({ status: 'loading' })
      const images = await onFetchPrintImages(s3ObjectIds)
      if (isNull(images)) {
        setPrintState({ status: 'error' })
        return
      }
      setPrintState({ status: 'ready', millsheets: selected, images })
    } finally {
      setBulkPending(false)
    }
  }

  // 列定義。証明書番号（no）は v1 と同じく左固定の別列として扱う（表示列メニューの対象外）。
  // 残りは columns（MillsheetColumnKey[]）から生成する
  const dataTableColumns: Array<DataTableColumn<MillsheetRow>> = [
    {
      key: 'no',
      title: MILLSHEET_COLUMN_LABELS.no,
      sortable: true,
      fixed: 'left',
      minWidthClass: 'min-w-40',
      keyColumn: true,
      // 行の展開（明細のインライン編集）は残したまま、名前列から詳細画面へ入る
      render: (row): ReactNode => (
        <DataTableLink
          to="/t/$tenant_id/millsheets/$millsheet_id"
          params={{ tenant_id: row.tenantId, millsheet_id: row.id }}
        >
          {valueOrDash(row.no)}
        </DataTableLink>
      ),
    },
    ...columns.map((key): DataTableColumn<MillsheetRow> => ({
      key,
      title: MILLSHEET_COLUMN_LABELS[key],
      // ファイル系列（URL自体に順序の意味がない）と actions は元々ソート対象外（現行挙動を維持）
      sortable:
        key !== 'actions' && key !== 'fileUrl' && key !== 'attachmentFileUrl' && key !== 'shearingCertificateFileUrl',
      hidable: key !== 'actions',
      // renderCell の戻り値型 ReactNode は React 19 で Promise<AwaitedReactNode> を含む Union になり、
      // 明示の戻り値型注釈が無いと @typescript-eslint/promise-function-async が誤検知する
      // （renderCell 自体が Promise を返すわけではない。orders-table.tsx と同じ回避策）
      render: (row): ReactNode =>
        renderCell(key, row, {
          onDownloadS3Object,
          onUpdateMillsheet,
          onRequestDelete: () => {
            setDeleteTargetId(row.id)
          },
        }),
    })),
  ]

  const rowSelection: DataTableRowSelection<MillsheetRow> = {
    isSelected: (row) => isSelected(row.id),
    allPageSelected,
    somePageSelected,
    onToggleSelectAllPage: toggleSelectAllPage,
    onToggleRow: (row) => {
      toggleSelect(row.id)
    },
    rowLabel: (row) => `${row.no} を選択`,
  }

  const expandable: DataTableExpandable<MillsheetRow> = {
    isExpanded: (row) => expandedIds.includes(row.id),
    onToggle: (row) => {
      toggleExpand(row.id)
    },
    expandedRowTitle: (row) => (
      <>
        {row.no} の明細
        <span className="ml-2 text-xs font-medium text-muted-foreground">{row.millsheetItems.length}件</span>
      </>
    ),
    expandedRowRender: (row) => <ExpandedItems row={row} onUpdateMillsheetItem={onUpdateMillsheetItem} />,
    detailsId: (row) => `millsheet-details-${row.id}`,
    // 展開トグルの aria-label は従来どおり証明書番号ベースを維持する（rowKey は React key 専用に切り離す）
    rowLabel: (row) => row.no,
  }

  // 成功時は結果パネルを出さずトーストのみで知らせる（docs/develop/client_ui/screens/states.md「操作の結果は「成功はトースト、失敗はトーストと画面の両方」」
  // 2026/08/30 FB）。Feedback 型は失敗（'error'）に narrow 済み
  // のため、ここで実行時に success を除外するフィルタは不要
  const errorFeedback = bulkFeedback ?? feedback

  return (
    <>
      <DataTable
        columns={dataTableColumns}
        rows={millsheets}
        // 証明書番号（no）は裏書運用で同一値の複数行が正規データとして存在しうる（ユニークキーは
        // [tenantId, no, constructionName, orderNumber]）ため React key には使えない。
        // React key・行 testid はどちらも id ベースにし、業務キーは expandable.rowLabel 側にのみ渡す
        rowKey={(row) => row.id}
        rowTestId={(row) => `millsheet-row-${row.id}`}
        testIdPrefix="millsheet"
        loading={loading}
        emptyMessage="ミルシートがありません"
        sorters={sorters}
        onChangeSorters={onChangeSorters}
        rowSelection={rowSelection}
        expandable={expandable}
        selectionActions={
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              data-testid="millsheet-bulk-edit"
              disabled={bulkPending || actionsDisabled}
              onClick={() => {
                void openBulkEdit()
              }}
            >
              <PencilLine className="size-3.5" />
              一括編集
            </Button>
            <Button
              type="button"
              size="sm"
              className="gap-1.5"
              data-testid="millsheet-print"
              disabled={bulkPending || actionsDisabled}
              onClick={() => {
                void openPrint()
              }}
            >
              <Printer className="size-3.5" />
              印刷する
            </Button>
          </>
        }
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        banners={
          <>
            {isSome(errorFeedback) && (
              <ResultBanner
                variant="strip"
                status="error"
                message={errorFeedback.message}
                testId="millsheet-feedback"
                onDismiss={() => {
                  setBulkFeedback(null)
                  onDismissFeedback()
                }}
              />
            )}

            {/* ページ跨ぎの選択は「全 N 件を選択」バナー経由でのみ発生させる（2026/08/26 FB）。
              allPageSelected は「表示中ページ全選択」の意味 */}
            {totalPages > 1 && allPageSelected && (
              <SelectAllResultsBanner
                entityLabel="ミルシート"
                totalCount={pagination.totalCount}
                pageCount={millsheets.length}
                selectionMode={selection.mode}
                onSelectAllResults={selectAllResults}
                onClearSelection={clearSelection}
                testIdPrefix="millsheet"
              />
            )}
          </>
        }
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          onChangePage,
          // 1ページ50〜100行あり、下端までスクロールしないとページを送れないため上部にも出す
          showAtTop: true,
        }}
      />

      <ConfirmDialog
        open={isSome(deleteTargetId)}
        title="ミルシートを削除しますか？"
        message="この操作は取り消せません。紐付いていた PDF・明細も一覧から消えます。"
        confirmTestId="millsheet-delete-confirm"
        onClose={() => {
          setDeleteTargetId(null)
        }}
        onConfirm={() => {
          if (isNull(deleteTargetId)) {
            return
          }
          const id = deleteTargetId
          setDeleteTargetId(null)
          void onDelete(id).then((ok) => {
            if (ok) {
              deselect(id)
            }
          })
        }}
      />

      <BulkEditDialog
        open={bulkEditOpen}
        selectedCount={confirmedCount}
        onClose={() => {
          setBulkEditOpen(false)
          setConfirmedTarget(null)
        }}
        onSubmit={(fields) => {
          if (!isSome(confirmedTarget)) {
            return
          }
          const target = confirmedTarget
          setBulkEditOpen(false)
          setConfirmedTarget(null)
          void onBulkEdit(target, fields, confirmedCount)
        }}
      />

      <MillsheetPrintDialog
        open={printState.status !== 'closed'}
        state={printState}
        selectedCount={printState.status === 'closed' ? selectedCount : confirmedPrintCount}
        millsheetSortConfig={millsheetSortConfig}
        onClose={() => {
          setPrintState({ status: 'closed' })
          setConfirmedPrintCount(0)
        }}
      />
    </>
  )
}

function renderCell(
  key: MillsheetColumnKey,
  row: MillsheetRow,
  ctx: {
    onDownloadS3Object: (s3ObjectId: string) => Promise<string | null>
    onUpdateMillsheet: MillsheetsTablePropsUpdate
    onRequestDelete: () => void
  },
): ReactNode {
  switch (key) {
    case 'constructionName':
      return valueOrDash(row.constructionName)
    case 'constructionArea':
      return <AggregatedCell value={row.constructionArea} />
    case 'member':
      return <AggregatedCell value={row.member} />
    case 'buildingPart':
      return <AggregatedCell value={row.buildingPart} />
    case 'steelTypes':
      return valueOrDash(row.steelTypes.join(', '))
    case 'sectionTypes':
      return valueOrDash(row.sectionTypes.join(', '))
    case 'manufacturerName':
      return valueOrDash(row.manufacturerName)
    case 'tradingCompany':
      return valueOrDash(row.tradingCompany)
    case 'issuedDate':
      return valueOrDash(row.issuedDate.slice(0, 10))
    case 'no':
      // 証明書番号はキー列（左固定セル）として描画するため、通常列としては呼ばれない。
      // switch の網羅性を保つために残す
      return valueOrDash(row.no)
    case 'orderNumber':
      return valueOrDash(row.orderNumber)
    case 'innerNo':
      return (
        <InlineTextInput
          defaultValue={row.innerNo}
          ariaLabel="内部番号"
          onCommit={(next) => {
            void ctx.onUpdateMillsheet(row.id, { innerNo: next })
          }}
        />
      )
    case 'sizes':
      return valueOrDash(row.sizes.join(', '))
    case 'totalWeight':
      return <span className="tabular-nums">{row.totalWeight.toLocaleString('ja-JP')}</span>
    case 'fileUrl': {
      const millsheetPdfs = row.s3Objects.filter((x) => x.resourceType === 'millsheets').map((x) => x.id)
      return <PdfCountButton ids={millsheetPdfs} fileName="ミルシートPDF" onDownload={ctx.onDownloadS3Object} />
    }
    case 'attachmentFileUrl': {
      const attachmentPdfs = row.s3Objects.filter((x) => x.resourceType === 'millsheetAttachments').map((x) => x.id)
      return <PdfCountButton ids={attachmentPdfs} fileName="添付資料PDF" onDownload={ctx.onDownloadS3Object} />
    }
    case 'shearingCertificateFileUrl':
      return row.shearingCertificates.length > 0 ? (
        <span className="tabular-nums">{row.shearingCertificates.length}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      )
    case 'customer':
      return valueOrDash(row.customer)
    case 'submissionDate':
      return (
        <CompactDateInput
          defaultValue={isPresent(row.submissionDate) ? row.submissionDate.slice(0, 10) : ''}
          aria-label="提出日"
          className="h-9 rounded-lg px-2 text-xs"
          wrapperClassName="max-w-40"
          onBlur={(e) => {
            const next = e.target.value === '' ? null : e.target.value
            const current = isPresent(row.submissionDate) ? row.submissionDate.slice(0, 10) : null
            if (next !== current) {
              void ctx.onUpdateMillsheet(row.id, { submissionDate: next })
            }
          }}
        />
      )
    case 'steelOrder':
      return valueOrDash(row.steelOrderReferenceNumber)
    case 'actions':
      return (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive"
          aria-label="ミルシートを削除"
          data-testid={`millsheet-delete-${row.id}`}
          onClick={ctx.onRequestDelete}
        >
          <Trash2 className="size-4" />
        </Button>
      )
    default: {
      const _: never = key
      return _
    }
  }
}

type MillsheetsTablePropsUpdate = (
  id: string,
  fields: { innerNo?: string; submissionDate?: string | null },
) => Promise<boolean>

function ExpandedItems({
  row,
  onUpdateMillsheetItem,
}: {
  row: MillsheetRow
  onUpdateMillsheetItem: (id: string, fields: MillsheetItemUpdateFields) => Promise<boolean>
}) {
  if (row.millsheetItems.length === 0) {
    return <p className="text-sm text-muted-foreground">明細がありません</p>
  }
  const itemColumns: Array<DataTableColumn<MillsheetRow['millsheetItems'][number]>> = [
    { key: 'size', title: '寸法', render: (item): ReactNode => valueOrDash(item.size) },
    {
      key: 'thicknessDiameter',
      title: '厚・径',
      render: (item) => (
        <InlineTextInput
          defaultValue={item.thicknessDiameter ?? ''}
          ariaLabel="厚・径"
          onCommit={(next) => {
            void onUpdateMillsheetItem(item.id, { thicknessDiameter: next })
          }}
        />
      ),
    },
    { key: 'length', title: '長さ', numeric: true, render: (item): ReactNode => valueOrDash(item.length) },
    { key: 'quantity', title: '数量', numeric: true, render: (item) => item.quantity },
    {
      key: 'weight',
      title: '重量',
      numeric: true,
      render: (item): ReactNode => valueOrDash(item.weightBeforeProcessing),
    },
    { key: 'heatNumber', title: '製鋼番号', render: (item): ReactNode => valueOrDash(item.heatNumber) },
    { key: 'individualNumber', title: '個体番号', render: (item): ReactNode => valueOrDash(item.individualNumber) },
    {
      key: 'endorsementCompany',
      title: '裏書会社',
      render: (item) => (
        <InlineTextInput
          defaultValue={item.endorsementCompany ?? ''}
          ariaLabel="裏書会社"
          onCommit={(next) => {
            void onUpdateMillsheetItem(item.id, { endorsementCompany: next === '' ? null : next })
          }}
        />
      ),
    },
    {
      key: 'endorsementDate',
      title: '裏書日',
      render: (item) => (
        <CompactDateInput
          defaultValue={isPresent(item.endorsementDate) ? item.endorsementDate.slice(0, 10) : ''}
          aria-label="裏書日"
          className="h-9 rounded-lg px-2 text-xs"
          wrapperClassName="max-w-40"
          onBlur={(e) => {
            const next = e.target.value === '' ? null : e.target.value
            const current = isPresent(item.endorsementDate) ? item.endorsementDate.slice(0, 10) : null
            if (next !== current) {
              void onUpdateMillsheetItem(item.id, { endorsementDate: next })
            }
          }}
        />
      ),
    },
    {
      key: 'constructionArea',
      title: '工区',
      render: (item) => (
        <InlineTextInput
          defaultValue={item.constructionArea}
          ariaLabel="工区"
          onCommit={(next) => {
            void onUpdateMillsheetItem(item.id, { constructionArea: next })
          }}
        />
      ),
    },
    {
      key: 'member',
      title: '節',
      render: (item) => (
        <InlineTextInput
          defaultValue={item.member}
          ariaLabel="節"
          onCommit={(next) => {
            void onUpdateMillsheetItem(item.id, { member: next })
          }}
        />
      ),
    },
    {
      key: 'buildingPart',
      title: '部位',
      render: (item) => (
        <InlineTextInput
          defaultValue={item.buildingPart}
          ariaLabel="部位"
          onCommit={(next) => {
            void onUpdateMillsheetItem(item.id, { buildingPart: next })
          }}
        />
      ),
    },
  ]
  return (
    <DataTable
      columns={itemColumns}
      rows={row.millsheetItems}
      rowKey={(item) => item.id}
      rowTestId={(item) => `millsheet-item-row-${item.id}`}
      rowHighlighted={(item) => item.isMatched}
      testIdPrefix="millsheet-items"
    />
  )
}
